import { Injectable } from '@angular/core';
import { AgricultureMapConstants, LAYER_TYPES } from 'src/app/main/constants/dims-map-constants';
import * as L from 'leaflet';
import { LOCATION_HIE, SATELLITE_LAYER_LIST, SATELLITE_LEGEND_LIST } from '../constants/satellite-map-constants';
import { environment } from 'src/environments/environment';
import { SatelliteDataService } from '../services/satellite-data.service';
import { MapService } from 'src/app/utils/map.service';
import { isNullOrUndefined } from 'util';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { auditTime, catchError, distinctUntilChanged, map, switchMap } from 'rxjs/operators';

const SATELLITE_WMTS_LAYER = 'cloud_top_temperature:cloud_top_temperature';
const SATELLITE_WORKSPACE = 'cloud_top_temperature';
// Fixed source key used for the outer Map — satellite has no queryParams.source.
const SATELLITE_SOURCE_KEY = 'cloud_top_temperature';
// Gray Index inspector: WMTS tile size (EPSG:900913 gridset) and hover request interval.
const CTT_TILE_SIZE = 256;
const CTT_HOVER_INTERVAL_MS = 200;

@Injectable({
  providedIn: 'root'
})
export class SatelliteMapService {

  gisMapComp: any;
  activeGroup: any;
  selectedLocType:any;

  // Pre-loaded tile layer maps — mirrors seamless-temp-map.service.ts pattern.
  geoTiffLayers: Map<string, Map<string, any>> = new Map();
  geoTiffOverlayers: Map<string, Map<string, any>> = new Map();

  private cachedTimestampList: string[] = [];
  private readonly PAGE_SIZE = 12;
  allLabelStrings: any[] = [];
  currentPageStartIndex: number = 0;
  isOnMostRecentPage: boolean = true;
  private _ctpMouseOver: any = null;
  private _ctpMouseOut: any = null;

  // Gray Index inspector state (GetFeatureInfo against the active CTT timestamp).
  private inspectMap: any = null;
  private inspectHandlers: any = null;
  private inspectLeaveHandler: any = null;
  private inspectSubs: Subscription[] = [];
  private hoverQuery$ = new Subject<any>();
  private hoverTooltip: any = null;
  private hoverLatLng: any = null;
  private hoverKey: string = null;
  private hoverShownKey: string = null;
  private keydownHandler: any = null;

  // Right-click context menu and distance measurement state.
  private contextMenuEl: HTMLElement = null;
  private contextMenuLatLng: any = null;
  private measuring = false;
  private measurePoints: any[] = [];
  private measureGroup: any = null;
  private measureLabels: any = null;
  private measureLine: any = null;
  private measureRubberLine: any = null;
  private measureCursorTooltip: any = null;
  private savedOnLocationClick: any = null;
  private savedDoubleClickZoom = false;

  constructor(private satelliteDataSrv: SatelliteDataService) { }

  async getSrcWiseLayers(gisMapComp: any) {
    this.gisMapComp = gisMapComp;
    // Reset so GeoJSON boundary layers are re-added to the new map instance on re-navigation.
    this.selectedLocType = null;
    let queryParams = gisMapComp.params;
    gisMapComp.legendList = {};
    gisMapComp.showThrobber = true;
    gisMapComp.map.closePopup();
    gisMapComp.dataTabToggleVal = false

    gisMapComp.getAndSetBaseLayers(2);
    this.initGrayIndexInspector(gisMapComp);

    if (this.cachedTimestampList.length === 0) {
      this.cachedTimestampList = await this.fetchTimestamps();
    }
    // Always reload tile layers onto the (potentially new) map instance.
    await this.loadGeoTiffLayer(gisMapComp, queryParams, this.cachedTimestampList);
    if (gisMapComp.labelStrings.length > 0) {
      gisMapComp.playPosition = gisMapComp.labelStrings.length - 1;
    }
    // activeGroup was reset to the latest timestamp — re-query the hover value for it.
    this.refreshGrayIndexInspector();

    const hie = LOCATION_HIE;
    const layerName = gisMapComp.gisUtils.titleCase(hie[hie.indexOf(queryParams.locType) + 1]);
    let layerList: any = {};
    layerList[layerName + ' Boundary'] = SATELLITE_LAYER_LIST[AgricultureMapConstants.LOCATIONS.INDIA][layerName + ' Boundary'];
    layerList[layerName + ' Boundary Labels'] = SATELLITE_LAYER_LIST[AgricultureMapConstants.LOCATIONS.INDIA][layerName + ' Boundary Labels'];
    layerList[gisMapComp.gisUtils.titleCase(queryParams.locType)] = SATELLITE_LAYER_LIST[AgricultureMapConstants.LOCATIONS.INDIA][gisMapComp.gisUtils.titleCase(queryParams.locType) + ' Boundaries'];
    if (queryParams.locType != 'COUNTRY') {
      /*Adding mask layer */
      layerList = gisMapComp.gisdataService.addMaskLayer(layerList, queryParams, gisMapComp.params.component)
    }

    this.addSrcWiseGeoJSONLayers(gisMapComp, layerList, queryParams);
    this.addSrcWiseVectorLayers(layerList, gisMapComp, queryParams);
    this.addSrcWiseWMSLayers(layerList, gisMapComp, queryParams);

    gisMapComp.otherOptions.onLocationClick = queryParams['locType'] === 'DISTRICT' ? false : true;
    if (this.measuring) {
      // Keep drill-down suspended while a measurement is in progress; restore this value afterwards.
      this.savedOnLocationClick = gisMapComp.otherOptions.onLocationClick;
      gisMapComp.otherOptions.onLocationClick = false;
    }

    this.addLegend(gisMapComp);
      }

  addSrcWiseGeoJSONLayers(gisMapComp: any, layerList: any, queryParams: any) {
    if(queryParams.locType === this.selectedLocType) return;
    this.selectedLocType = queryParams.locType;
    let geoJsonLayers = gisMapComp.gisdataService.getApplicationBasedLayer(layerList, LAYER_TYPES.GEOJSON_LAYER);
    geoJsonLayers = this.formatLayersData(gisMapComp, geoJsonLayers);
    gisMapComp.addSrcWiseGEOJSONLayers(geoJsonLayers);
  }

  formatLayersData(gisMapComp: any, layerList: any) {
    for (const layerName in layerList) {
      if (layerList[layerName].layerOptions.cql_filter_value) {
        layerList[layerName].layerOptions.cql_filter =
          layerList[layerName].layerOptions.cql_filter_value + "='" + gisMapComp.params['pUUID'] + "'";
      }
    }
    return layerList;
  }

  async addSrcWiseVectorLayers(layerList: any, gisMapComp: any, queryParams: any) {
    let vectorLayersBasedOnView = gisMapComp.gisdataService.getApplicationBasedLayer(layerList, LAYER_TYPES.VECTOR_LAYER);
    vectorLayersBasedOnView = this.addSourceWiseLayers(gisMapComp, vectorLayersBasedOnView, queryParams);
    for (const layer in layerList) {
      if (layerList[layer].applycql_filter) {
        vectorLayersBasedOnView = this.formatVectorLayersData(vectorLayersBasedOnView, queryParams, gisMapComp);
      }
    }
    this.setVectorLayerStyles(vectorLayersBasedOnView, gisMapComp);
    gisMapComp.addSrcWiseVECTORLayers(vectorLayersBasedOnView);
  }

  formatVectorLayersData(layerList: any, queryParams: any, gisMapComp: any) {
    for (const layerName in layerList) {
      const uuid = queryParams['pUUID'];
      if (queryParams.locType === 'COUNTRY' && layerName === 'State Boundary') {
        layerList[layerName].layerOptions.cql_filter_value = null;
        layerList[layerName].layerOptions.cql_filter = '1=1';
      }
      if (queryParams.locType === 'STATE' && layerName === 'District Boundary') {
        layerList[layerName].layerOptions.cql_filter_value = 'state_uuid';
        layerList[layerName].layerOptions.cql_filter = "state_uuid='" + uuid + "'";
      }
      if (queryParams.locType === 'DISTRICT' && layerName === 'Block Boundary') {
        layerList[layerName].layerOptions.cql_filter_value = 'dist_uuid';
        layerList[layerName].layerOptions.cql_filter = "dist_uuid='" + uuid + "'";
      }
    }
    return layerList;
  }

  setVectorLayerStyles(layerList: any, gisMapComp: any) {
    for (const key in layerList) {
      const layerName = layerList[key]['layerOptions']['layerName'].split(':')[1];
      layerList[key].layerOptions.vectorTileLayerStyles[layerName] = function(properties: any) {
        const hie: any = AgricultureMapConstants.ADMIN_BOUNDARIES_HIE;
        const currentLevel: any = hie[hie.indexOf(gisMapComp.params.locType) + 1];
        const levelBoundary = gisMapComp.gisUtils.titleCase(gisMapComp.params.locType) + ' Boundaries';
        gisMapComp.map.eachLayer((layer: any) => {
          if (layer.options.layer_name === levelBoundary && gisMapComp.map.hasLayer(layer)) {
            gisMapComp.map.removeLayer(layer);
          }
        });
        const style: any = {
          color: '#fff',
          fill: false,
          weight: 0.8,
          opacity: 0.8,
          fillColor: 'transparent',
          fillOpacity: 0.5
        };
        return style;
      };
    }
  }

  addSourceWiseLayers(gisMapComp: any, layerList: any, params: any) {
    const layers: any = {};
    for (const i in layerList) {
      layers[i] = layerList[i];
      gisMapComp.activeLayers[i] = layerList[i].layerOptions.visible;
    }
    return layers;
  }

  /**
   * Pre-loads one tile layer per timestamp onto the map.
   * Only the latest (last) timestamp is visible; all others are hidden via display:none.
   * Mirrors loadGeoTiffLayer() from seamless-temp-map.service.ts.
   */
  loadGeoTiffLayer(gisMapComp: any, queryParams: any, timeStampList: string[]): Promise<any> {
    return new Promise((resolve) => {
      gisMapComp.labelStrings = [];
      this.allLabelStrings = [];
      this.removePreviousLayersFromMap(gisMapComp);
      this.geoTiffLayers.set(SATELLITE_SOURCE_KEY, new Map());
      this.geoTiffOverlayers.set(SATELLITE_SOURCE_KEY, new Map());

      if (!timeStampList || timeStampList.length === 0) {
        this.currentPageStartIndex = 0;
        this.isOnMostRecentPage = true;
        resolve(true);
        return;
      }

      const lastIndex = timeStampList.length - 1;

      for (let i = 0; i < timeStampList.length; i++) {
        const ts = timeStampList[i];
        const groupName = ts;
        const label = this.satelliteDataSrv.formatTimestamp(ts);

        const layerConfig: any = {};
        layerConfig[groupName] = {
          layeroptions_name: SATELLITE_WMTS_LAYER,
          cql_filter: `time='${ts}'`
        };

        const overlayResult = this.setRasterWMTSOverlays(gisMapComp, gisMapComp.map, layerConfig);

        this.allLabelStrings.push({
          groupName,
          label: { label, toolTip: label },
          fullLabel: label,
          type: 'Observed'
        });

        const vectorLayer = overlayResult[groupName];
        if (!vectorLayer) { continue; }

        this.geoTiffLayers.get(SATELLITE_SOURCE_KEY).set(groupName, vectorLayer);
        const addedLayer = vectorLayer.addTo(gisMapComp.map);
        addedLayer.on('load', () => {
          gisMapComp.showThrobber = false;
        })

        const container = addedLayer.getContainer();
        if (container) {
          container.style.display = i === lastIndex ? 'block' : 'none';
        }

        if (i === lastIndex) {
          gisMapComp.activeGroup = groupName;
          this.activeGroup = groupName;
        }

        this.geoTiffOverlayers.get(SATELLITE_SOURCE_KEY).set(groupName, addedLayer);
      }

      // Paginate: show only the last PAGE_SIZE timestamps by default
      const total = this.allLabelStrings.length;
      this.currentPageStartIndex = Math.max(0, total - this.PAGE_SIZE);
      this.isOnMostRecentPage = true;
      gisMapComp.labelStrings = [...this.allLabelStrings.slice(this.currentPageStartIndex)];

      if (gisMapComp.labelStrings.length > 0) {
        const lastLabel = gisMapComp.labelStrings[gisMapComp.labelStrings.length - 1];
        gisMapComp.initialCounter = parseInt(lastLabel['groupName'], 10);
      }

      resolve(true);
    });
  }

  /**
   * Creates L.tileLayer instances for each groupName in vectorLayers.
   * Mirrors setRasterWMTSOverlays() from seamless-temp-map.service.ts.
   */
  setRasterWMTSOverlays(gisMapComp: any, mapObj: any, vectorLayers: any): any {
    const layers: any = {};
    for (const key in vectorLayers) {
      if (!vectorLayers.hasOwnProperty(key)) { continue; }
      const layer = vectorLayers[key];
      const url = this.getVectorLayerURL(layer.layeroptions_name, layer.cql_filter);
      try {
        const tileLayer = L.tileLayer(url, {
          zIndex: 2,
          opacity: 1,
          maxZoom: 22,
          minZoom: 3,
          attribution: 'CTT'
        } as any);
        layers[key] = tileLayer;
      } catch (e) {
        console.error('Error creating CTT tile layer:', e);
      }
    }
    return layers;
  }

  /**
   * Builds the WMTS tile URL.
   * Mirrors getVectorLayerURL() from seamless-temp-map.service.ts.
   */
  private getVectorLayerURL(layerName: string, cqlFilter: string): string {
    const workspace = layerName.includes(':') ? layerName.split(':')[0] : SATELLITE_WORKSPACE;
    let url = environment.url.COMMON_GEOSERVER_URL + workspace;
    url += '/gwc/service/wmts?layer=' + layerName
      + '&tilematrixset=EPSG:900913&Service=WMTS&Request=GetTile&Version=1.0.0'
      + '&Format=image/png'
      + '&TileMatrix=EPSG:900913:{z}&TileCol={x}&TileRow={y}';
    if (cqlFilter) {
      url += '&cql_filter=' + cqlFilter;
    }
    return url;
  }

  /** Removes all pre-loaded CTT tile layers from the map. */
  removePreviousLayersFromMap(gisMapComp: any) {
    for (const [, innerMap] of Array.from(this.geoTiffLayers.entries())) {
      for (const [groupName] of Array.from(innerMap.entries())) {
        const layer = innerMap.get(groupName);
        if (layer && gisMapComp.map.hasLayer(layer)) {
          gisMapComp.map.removeLayer(layer);
        }
      }
    }
  }

  /** Hides a timestamp group's tile container (display: none). */
  private removeGroupTimeTiffLayers(gis: any, groupName: any) {
    const outerMap = this.geoTiffOverlayers.get(SATELLITE_SOURCE_KEY);
    if (!outerMap) { return; }
    outerMap.forEach((value, key) => {
      if (key === groupName) {
        const container = value.getContainer();
        if (container) { container.style.display = 'none'; }
      }
    });
  }

  /** Shows a timestamp group's tile container (display: block). */
  private addGroupTimeTiffLayers(gis: any, groupName: any) {
    const outerMap = this.geoTiffOverlayers.get(SATELLITE_SOURCE_KEY);
    if (!outerMap) { return; }
    outerMap.forEach((value, key) => {
      if (key === groupName) {
        const container = value.getContainer();
        if (container) { container.style.display = 'block'; }
      }
    });
  }

  /**
   * Toggles tile visibility when the play bar advances to a new timestamp.
   * No setUrl() calls — all layers are pre-loaded, toggled via CSS display.
   */
  changeTimeLayers(gis: any, groupName: any) {
    if (groupName !== gis.activeGroup) {
      const oldGroupName = gis.activeGroup;
      gis.activeGroup = groupName;
      this.activeGroup = groupName;
      // this.selectedTimestamp = groupName;
      this.removeGroupTimeTiffLayers(gis, oldGroupName);
      this.addGroupTimeTiffLayers(gis, groupName);
      this.refreshGrayIndexInspector();
    }
  }

  /**
   * Called by SatelliteSidebarComponent when the user selects a date.
   * Clears existing layers and reloads with the new date's timestamps.
   */
  async selectDate(dateKey: string) {
    if (!this.gisMapComp || !dateKey) { return; }
    if (this.satelliteDataSrv.selectedDateKey === dateKey) { return; }
    this.satelliteDataSrv.selectedDateKey = dateKey;

    const timestamps = this.satelliteDataSrv.groupedTimestamps[dateKey] || [];
    if (timestamps.length === 0) { return; }

    this.cachedTimestampList = [...timestamps];
    this.gisMapComp.showThrobber = true;
    const queryParams: any = this.gisMapComp['params'] || {};
    await this.loadGeoTiffLayer(this.gisMapComp, queryParams, timestamps);
    this.gisMapComp.playPosition = this.gisMapComp.labelStrings.length - 1;
    this.gisMapComp.showThrobber = false;
    this.refreshGrayIndexInspector();
  }

  /**
   * Called when the user presses play from a stopped state.
   * Resets to page 0 (oldest timestamps) so playback always begins from the
   * start of the day, matching the expected UX in the satellite module.
   */
  onPlayStarted() {
    if (!this.gisMapComp) { return; }
    const total = this.allLabelStrings.length;
    if (total === 0) { return; }
    this.currentPageStartIndex = 0;
    this.isOnMostRecentPage = total <= this.PAGE_SIZE;
    this.gisMapComp.labelStrings = [...this.allLabelStrings.slice(0, this.PAGE_SIZE)];
  }

  /**
   * Called by GisDimsMapComponent when the play bar animation reaches the end
   * of the current labelStrings page. Advances to the next page, or wraps to
   * the first page when the last page is exhausted, so the play bar continuously
   * loops through the entire day's timestamps.
   */
  onAnimationPageEnd() {
    if (!this.gisMapComp) { return; }
    const total = this.allLabelStrings.length;
    if (total <= this.PAGE_SIZE) { return; }
    const nextStart = this.currentPageStartIndex + this.PAGE_SIZE;
    this.currentPageStartIndex = nextStart >= total ? 0 : nextStart;
    const mostRecentStart = Math.max(0, total - this.PAGE_SIZE);
    this.isOnMostRecentPage = this.currentPageStartIndex === mostRecentStart;
    this.gisMapComp.labelStrings = [...this.allLabelStrings.slice(
      this.currentPageStartIndex,
      this.currentPageStartIndex + this.PAGE_SIZE
    )];
  }

  /** Navigate to the previous (older) page of 12 timestamps. */
  prevPage() {
    if (!this.gisMapComp || this.currentPageStartIndex === 0) { return; }
    this.currentPageStartIndex = Math.max(0, this.currentPageStartIndex - this.PAGE_SIZE);
    const mostRecentStart = Math.max(0, this.allLabelStrings.length - this.PAGE_SIZE);
    this.isOnMostRecentPage = this.currentPageStartIndex === mostRecentStart;
    this.gisMapComp.labelStrings = [...this.allLabelStrings.slice(
      this.currentPageStartIndex,
      this.currentPageStartIndex + this.PAGE_SIZE
    )];
    if (this.gisMapComp.labelStrings.length > 0) {
      const lastLabel = this.gisMapComp.labelStrings[this.gisMapComp.labelStrings.length - 1];
      this.changeTimeLayers(this.gisMapComp, lastLabel.groupName);
      this.gisMapComp.playPosition = this.gisMapComp.labelStrings.length - 1;
    }
  }

  /** Navigate to the next (more recent) page of 12 timestamps. */
  nextPage() {
    if (!this.gisMapComp) { return; }
    const total = this.allLabelStrings.length;
    const mostRecentStart = Math.max(0, total - this.PAGE_SIZE);
    if (this.currentPageStartIndex >= mostRecentStart) { return; }
    this.currentPageStartIndex = Math.min(this.currentPageStartIndex + this.PAGE_SIZE, mostRecentStart);
    this.isOnMostRecentPage = this.currentPageStartIndex === mostRecentStart;
    this.gisMapComp.labelStrings = [...this.allLabelStrings.slice(
      this.currentPageStartIndex,
      this.currentPageStartIndex + this.PAGE_SIZE
    )];
    if (this.gisMapComp.labelStrings.length > 0) {
      const lastLabel = this.gisMapComp.labelStrings[this.gisMapComp.labelStrings.length - 1];
      this.changeTimeLayers(this.gisMapComp, lastLabel.groupName);
      this.gisMapComp.playPosition = this.gisMapComp.labelStrings.length - 1;
    }
  }

  /**
   * Fetches CTP timestamps from the API, builds grouped data for the sidebar,
   * and returns the timestamp list for the most recent (active) date.
   */
  private async fetchTimestamps(): Promise<string[]> {
    try {
      const response: any = await this.satelliteDataSrv.getCTPTimestamps().toPromise();
      const rawList: string[] = Array.isArray(response)
        ? response.filter(r => typeof r === 'string')
        : [];

      if (rawList.length > 0) {
        this.satelliteDataSrv.buildGroupedData(rawList);
        const existingKey = this.satelliteDataSrv.selectedDateKey;
        const activeDate = (existingKey && this.satelliteDataSrv.groupedTimestamps[existingKey])
          ? existingKey
          : this.satelliteDataSrv.getActiveDate();
        this.satelliteDataSrv.selectedDateKey = activeDate;
        return this.satelliteDataSrv.groupedTimestamps[activeDate] || [];
      }
    } catch (e) {
      console.error('CTP timestamps fetch failed:', e);
    }

    this.satelliteDataSrv.allDates = [];
    this.satelliteDataSrv.groupedTimestamps = {};
    this.satelliteDataSrv.selectedDateKey = null;
    return [];
  }

  private addLegend(gisMapComp: any) {
    for (const key in SATELLITE_LEGEND_LIST[AgricultureMapConstants.LOCATIONS.INDIA]) {
      gisMapComp['legendList'][key] = SATELLITE_LEGEND_LIST[AgricultureMapConstants.LOCATIONS.INDIA][key];
    }
    gisMapComp.manageLegend();
    gisMapComp.gisUtils.setupLegendTooltip(this._ctpMouseOver, this._ctpMouseOut, 'ctp-legend-tooltip');
  }

  getHeirarchyData(gisMapComp: any) {
    const heirarchyObj = this.getHeirarchyObj(gisMapComp);
    let metaInfo: any = localStorage.getItem('metaInfo');
    let metaInfoJSON = JSON.parse(metaInfo);
    let userlocType: string;
    if (metaInfoJSON && metaInfoJSON.hasOwnProperty('userDetailsJson') && !isNullOrUndefined(metaInfoJSON.userDetailsJson)) {
      userlocType = metaInfoJSON.userDetailsJson.data.locType;
    } else {
      userlocType = 'Country';
    }
    userlocType = userlocType.toUpperCase();
    let locTypeIndex = LOCATION_HIE.indexOf(userlocType);
    const hie = LOCATION_HIE.slice(locTypeIndex, 3);
    this.satelliteDataSrv.getHeirarchy(heirarchyObj).subscribe(data => {
      gisMapComp.inputForBreadcrumb = [];
      if (!isNullOrUndefined(data) && Object.keys(data).length > 0) {
        data.forEach(element => {
          const obj = {
            locType: element['locType'],
            locationID: element['locationID'],
            locationName: element['ln'],
            locationUUID: element['lUUID'],
            clickable: hie.includes(element['locType'])
          };
          gisMapComp.inputForBreadcrumb.push(obj);
        });
        gisMapComp.inputForBreadcrumb = gisMapComp.inputForBreadcrumb.reverse();
      }
    });
  }

  getHeirarchyObj(gisMapComp: any) {
    const heirarchyObj: any = {};
    heirarchyObj['locName'] = gisMapComp.params['locName'];
    heirarchyObj['locUUID'] = gisMapComp.params['pUUID'];
    heirarchyObj['locType'] = gisMapComp.params['locType'];
    heirarchyObj['component'] = 'VL';
    heirarchyObj['view'] = 'ADMIN';
    heirarchyObj['pEUUID'] = 'cc88f822-8474-48d4-8327-4c85444e4a30';
    return heirarchyObj;
  }

  handleEventFromBreadcrumb(data: any) {
    const gisMapComp = this.gisMapComp;
    const currentUrlParams = { ...gisMapComp.params };
    const locType = data['selectedLocation']['locType'];
    const location = currentUrlParams['location'];
    const options = this.updateBreadCrumbOptions(locType, location, currentUrlParams);
    gisMapComp.gisUtils.setSelectedParams(options);
  }

  updateBreadCrumbOptions(locType: string, allLocations: string, options: any) {
    let location: string;
    if (locType === 'COUNTRY') {
      location = allLocations.split('&')[0];
      options['locType'] = 'COUNTRY';
      options['locName'] = location.split('##')[0];
      options['location'] = allLocations.split('&')[0];
      options['pUUID'] = location.split('##')[1];
    } else if (locType === 'STATE') {
      location = allLocations.split('&')[1];
      options['locType'] = 'STATE';
      options['locName'] = location.split('##')[0];
      options['location'] = allLocations.split('&')[0] + '&' + allLocations.split('&')[1];
      options['pUUID'] = location.split('##')[1];
    }
    return options;
  }

  handleAdminPolygons(layer: any, mapObj: any, params: any, gisMapComp: any) {
    layer.setStyle({ color: '#fff', fill: false, weight: 1.5 });
  }

  private addSrcWiseWMSLayers(layerList: any, gisMapComp: any, queryParams: any) {
    let wmsLayers = gisMapComp.gisdataService.getApplicationBasedLayer(layerList, LAYER_TYPES.WMS_LAYER);
    wmsLayers = this.addSourceWiseLayers(gisMapComp, wmsLayers, queryParams);
    for (const layer in layerList) {
      if (layerList[layer].applycql_filter) {
        wmsLayers = this.formatWmsLayersData(wmsLayers, queryParams);
      }
    }
    gisMapComp.addSrcWiseWMSLayers(wmsLayers);
  }

  formatWmsLayersData(layerList: any, queryParams: any) {
    for (const layerName in layerList) {
      const allLocations = queryParams.location ? queryParams.location.split('&') : [];
      if (queryParams.locType === 'STATE') {
        const stateUUID = allLocations[1] && allLocations[1].includes('##') ? allLocations[1].split('##')[1] : null;
        if (stateUUID && layerList[layerName].layerOptions.cql_filter_value) {
          layerList[layerName].layerOptions.cql_filter =
            layerList[layerName].layerOptions.cql_filter_value + "='" + stateUUID + "'";
        }
      }
      if (queryParams.locType === 'DISTRICT') {
        const districtUUID = allLocations[2] && allLocations[2].includes('##') ? allLocations[2].split('##')[1] : null;
        if (districtUUID && layerList[layerName].layerOptions.cql_filter_value) {
          layerList[layerName].layerOptions.cql_filter =
            layerList[layerName].layerOptions.cql_filter_value + "='" + districtUUID + "'";
        }
      }
              if (layerName.includes('world_view')) {
                // console.log(layerList,"layers list");
                let uuid: any = queryParams.pUUID;
                  layerList[layerName].layerOptions.cql_filter = '1=1;' + AgricultureMapConstants.LOCTYPE_CQLFILTERVALUE[queryParams.locType] + "='" + uuid + "'";
                 layerList[layerName].layerOptions.STYLES = "HEATWAVE:world_view_white,HEATWAVE:ctt_inverse_mask";
              }
    }
    return layerList;
  }

  /**
   * Wires the Gray Index inspector onto the current map instance:
   * hover shows a live value next to the cursor, right-click opens a context menu (Measure distance).
   * All are additive map listeners, so existing boundary click / drill-down handlers are untouched.
   */
  private initGrayIndexInspector(gisMapComp: any) {
    const mapObj = gisMapComp.map;
    if (!mapObj || this.inspectMap === mapObj) { return; }
    this.destroyGrayIndexInspector();
    this.inspectMap = mapObj;

    // Hover: emit at most once per interval, skip repeats of the same pixel/timestamp, and
    // switchMap so an in-flight request is cancelled once the cursor reaches a new pixel.
    this.inspectSubs.push(this.hoverQuery$.pipe(
      auditTime(CTT_HOVER_INTERVAL_MS),
      distinctUntilChanged((a: any, b: any) => !!a && !!b && a.key === b.key),
      switchMap((q: any) => q ? this.fetchGrayIndex(q) : of(null))
    ).subscribe((res: any) => this.renderHoverValue(res)));

    this.inspectHandlers = {
      mousemove: (e: any) => this.onInspectMouseMove(e),
      click: (e: any) => this.onInspectClick(e),
      dblclick: () => { if (this.measuring) { this.finishMeasure(); } },
      contextmenu: (e: any) => this.openContextMenu(e),
      movestart: () => this.closeContextMenu(),
      zoomstart: () => this.closeContextMenu()
    };
    mapObj.on(this.inspectHandlers);
    // Native mouseleave rather than Leaflet 'mouseout': boundary features' mouseout bubbles to the map.
    this.inspectLeaveHandler = () => {
      this.clearHover();
      if (this.measuring) { this.updateRubberLine(null); }
    };
    L.DomEvent.on(mapObj.getContainer(), 'mouseleave', this.inspectLeaveHandler);
    this.keydownHandler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') { return; }
      this.closeContextMenu();
      if (this.measuring) { this.finishMeasure(); }
    };
    document.addEventListener('keydown', this.keydownHandler);
  }

  /** Tears down listeners, overlays and pending requests. Also wired as the GIS map destroy hook. */
  destroyGrayIndexInspector(gis?: any) {
    this.clearMeasure();
    this.closeContextMenu();
    if (this.contextMenuEl && this.contextMenuEl.parentNode) {
      this.contextMenuEl.parentNode.removeChild(this.contextMenuEl);
    }
    this.contextMenuEl = null;
    if (this.inspectMap && this.inspectHandlers) {
      this.inspectMap.off(this.inspectHandlers);
      L.DomEvent.off(this.inspectMap.getContainer(), 'mouseleave', this.inspectLeaveHandler);
    }
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
    }
    this.clearHover();
    this.inspectSubs.forEach(sub => sub.unsubscribe());
    this.inspectSubs = [];
    this.inspectHandlers = null;
    this.inspectLeaveHandler = null;
    this.keydownHandler = null;
    this.inspectMap = null;
    this.hoverTooltip = null;
  }

  /** Re-queries the hover value after the active timestamp (or layers) change. */
  private refreshGrayIndexInspector() {
    if (!this.inspectMap || this.measuring) { return; }
    if (this.hoverLatLng) { this.queryHover(this.hoverLatLng); }
  }

  private onInspectMouseMove(e: any) {
    if (this.measuring) {
      this.updateRubberLine(e.latlng);
      return;
    }
    // Cursor is over the context menu — don't stack a hover tooltip on top of it.
    const target: any = e.originalEvent && e.originalEvent.target;
    if (target && target.closest && target.closest('.ctt-context-menu')) {
      this.clearHover();
      return;
    }
    this.queryHover(e.latlng);
  }

  private onInspectClick(e: any) {
    if (this.contextMenuEl && this.contextMenuEl.style.display !== 'none') {
      this.closeContextMenu();
      return;
    }
    if (this.measuring) { this.addMeasurePoint(e.latlng); }
  }

  private queryHover(latlng: any) {
    const q = this.buildGrayIndexQuery(latlng);
    if (!q) { this.clearHover(); return; }
    this.hoverLatLng = latlng;
    if (!this.hoverTooltip) {
      this.hoverTooltip = L.tooltip({
        direction: 'right',
        offset: [14, 0],
        pane: 'measuretool',
        className: 'ctt-gray-index-tooltip'
      } as any);
    }
    this.hoverTooltip.setLatLng(latlng);
    if (!this.inspectMap.hasLayer(this.hoverTooltip)) {
      this.hoverShownKey = null;
      this.hoverTooltip.setContent(this.grayIndexHtml(null, true));
      this.hoverTooltip.addTo(this.inspectMap);
    } else {
      // Dim the shown value whenever it belongs to a different pixel/timestamp than the cursor's.
      this.setLoadingClass(this.hoverTooltip, q.key !== this.hoverShownKey);
    }
    this.hoverKey = q.key;
    this.hoverQuery$.next(q);
  }

  private renderHoverValue(res: any) {
    // Only the response for the pixel currently under the cursor may be shown.
    if (!res || !this.hoverTooltip || res.key !== this.hoverKey) { return; }
    this.hoverShownKey = res.key;
    this.hoverTooltip.setContent(this.grayIndexHtml(res, false));
  }

  private clearHover() {
    this.hoverQuery$.next(null);
    this.hoverLatLng = null;
    this.hoverKey = null;
    this.hoverShownKey = null;
    if (this.hoverTooltip && this.inspectMap && this.inspectMap.hasLayer(this.hoverTooltip)) {
      this.inspectMap.removeLayer(this.hoverTooltip);
    }
  }

  /**
   * Builds the GetFeatureInfo inputs from the WMTS tile actually rendered under `latlng`:
   * the tile's EPSG:3857 extent as BBOX, 256x256 as WIDTH/HEIGHT and the pixel inside that tile as X/Y,
   * so the queried pixel is the one drawn on screen. Returns null when there is no active timestamp.
   */
  private buildGrayIndexQuery(latlng: any): any {
    const ts = this.gisMapComp ? this.gisMapComp.activeGroup : null;
    if (!this.inspectMap || !latlng || !ts) { return null; }
    const crs: any = this.inspectMap.options.crs;
    // Tile zoom as chosen by L.GridLayer for the current (possibly fractional) map zoom.
    const zoom = Math.round(this.inspectMap.getZoom());
    const scale = crs.scale(zoom);
    const pixel = crs.latLngToPoint(latlng.wrap(), zoom).floor();
    const tileOrigin = pixel.divideBy(CTT_TILE_SIZE).floor().multiplyBy(CTT_TILE_SIZE);
    const nw = crs.transformation.untransform(tileOrigin, scale);
    const se = crs.transformation.untransform(tileOrigin.add([CTT_TILE_SIZE, CTT_TILE_SIZE]), scale);
    return {
      key: ts + '|' + zoom + '|' + pixel.x + '|' + pixel.y,
      ts,
      latlng,
      bbox: [nw.x, se.y, se.x, nw.y].join(','),
      x: pixel.x - tileOrigin.x,
      y: pixel.y - tileOrigin.y
    };
  }

  private fetchGrayIndex(q: any): Observable<any> {
    return this.satelliteDataSrv.getCTTGrayIndex(
      SATELLITE_WMTS_LAYER, q.ts, q.bbox, CTT_TILE_SIZE, CTT_TILE_SIZE, q.x, q.y
    ).pipe(
      map((value: number) => ({ ...q, value, status: value === null ? 'nodata' : 'ok' })),
      catchError((err: any) => {
        console.error('CTT GetFeatureInfo failed:', err);
        return of({ ...q, value: null, status: 'error' });
      })
    );
  }

  private setLoadingClass(overlay: any, loading: boolean) {
    const el = overlay.getElement && overlay.getElement();
    const body = el && el.querySelector('.ctt-gi');
    if (body) { body.classList.toggle('ctt-gi-loading', loading); }
  }

  private grayIndexHtml(res: any, loading: boolean): string {
    let valueText = 'Loading…';
    let fullValue = '';
    if (!loading && res) {
      if (res.status === 'ok') {
        valueText = this.satelliteDataSrv.formatGrayIndex(res.value);
        fullValue = String(res.value);
      } else if (res.status === 'error') {
        valueText = 'Unable to load value';
      } else {
        valueText = 'No data';
      }
    }
    const valueClass = 'ctt-gi-value' + (res && res.status === 'ok' && !loading ? '' : ' ctt-gi-muted');
    return `<div class="ctt-gi"><span class="ctt-gi-label">Temp:</span> `
      + `<span class="${valueClass}" title="${fullValue}">${valueText}</span></div>`;
  }

  /** Shows the right-click menu at the cursor; it currently offers only "Measure distance". */
  private openContextMenu(e: any) {
    if (e.originalEvent) { L.DomEvent.preventDefault(e.originalEvent); }
    this.clearHover();
    const container = this.inspectMap.getContainer();
    if (!this.contextMenuEl) {
      const menu = L.DomUtil.create('div', 'ctt-context-menu', container);
      menu.innerHTML = '<div class="ctt-context-menu-item" data-action="measure">'
        + '<span class="ctt-context-menu-icon">&#128207;</span>Measure distance</div>';
      // Clicks/scrolls/right-clicks on the menu must not reach the map (no drill-down, no new menu).
      L.DomEvent.disableClickPropagation(menu);
      L.DomEvent.disableScrollPropagation(menu);
      L.DomEvent.on(menu, 'contextmenu', L.DomEvent.stop);
      L.DomEvent.on(menu, 'mousemove', L.DomEvent.stopPropagation);
      L.DomEvent.on(menu, 'click', (ev: any) => {
        const item = ev.target && ev.target.closest && ev.target.closest('[data-action="measure"]');
        if (item && this.contextMenuLatLng) {
          const start = this.contextMenuLatLng;
          this.closeContextMenu();
          this.startMeasure(start);
        }
      });
      this.contextMenuEl = menu;
    }
    this.contextMenuLatLng = e.latlng;
    const menuEl = this.contextMenuEl;
    menuEl.style.display = 'block';
    // Keep the menu inside the map container near the right/bottom edges.
    const size = this.inspectMap.getSize();
    const x = Math.min(e.containerPoint.x, size.x - menuEl.offsetWidth - 4);
    const y = Math.min(e.containerPoint.y, size.y - menuEl.offsetHeight - 4);
    menuEl.style.left = Math.max(0, x) + 'px';
    menuEl.style.top = Math.max(0, y) + 'px';
  }

  private closeContextMenu() {
    if (this.contextMenuEl) { this.contextMenuEl.style.display = 'none'; }
    this.contextMenuLatLng = null;
  }

  /**
   * Starts a distance measurement at `latlng`: each click adds a point, double-click or Esc finishes,
   * and the × on the total label removes it. Drill-down and double-click zoom are suspended meanwhile.
   */
  private startMeasure(latlng: any) {
    this.clearMeasure();
    const mapObj = this.inspectMap;
    this.measuring = true;
    this.measurePoints = [latlng];
    this.measureGroup = L.layerGroup().addTo(mapObj);
    this.measureLabels = L.layerGroup().addTo(this.measureGroup);
    this.measureLine = L.polyline([latlng], {
      pane: 'measuretool', color: '#ffffff', weight: 3, interactive: false
    }).addTo(this.measureGroup);
    this.measureRubberLine = L.polyline([], {
      pane: 'measuretool', color: '#ffffff', weight: 2, dashArray: '6 6', interactive: false
    }).addTo(this.measureGroup);

    this.savedOnLocationClick = this.gisMapComp.otherOptions.onLocationClick;
    this.gisMapComp.otherOptions.onLocationClick = false;
    this.savedDoubleClickZoom = mapObj.doubleClickZoom && mapObj.doubleClickZoom.enabled();
    if (this.savedDoubleClickZoom) { mapObj.doubleClickZoom.disable(); }
    L.DomUtil.addClass(mapObj.getContainer(), 'ctt-measuring');
    this.clearHover();
    this.renderMeasureLabels();
  }

  private addMeasurePoint(latlng: any) {
    const last = this.measurePoints[this.measurePoints.length - 1];
    // The two clicks of a double-click land on the same spot — don't add zero-length segments.
    if (this.inspectMap.latLngToContainerPoint(last).distanceTo(this.inspectMap.latLngToContainerPoint(latlng)) < 4) {
      return;
    }
    this.measurePoints.push(latlng);
    this.measureLine.setLatLngs(this.measurePoints);
    this.renderMeasureLabels();
    this.updateRubberLine(latlng);
  }

  /** Dashed segment from the last point to the cursor, with the running total next to the cursor. */
  private updateRubberLine(latlng: any) {
    if (!this.measuring || !this.measureRubberLine) { return; }
    if (!latlng) {
      this.measureRubberLine.setLatLngs([]);
      if (this.measureCursorTooltip) { this.measureGroup.removeLayer(this.measureCursorTooltip); }
      return;
    }
    const last = this.measurePoints[this.measurePoints.length - 1];
    this.measureRubberLine.setLatLngs([last, latlng]);
    if (!this.measureCursorTooltip) {
      this.measureCursorTooltip = L.tooltip({
        direction: 'right', offset: [14, 0], pane: 'measuretool', className: 'ctt-measure-label'
      } as any);
    }
    const total = this.measureDistance(this.measurePoints) + this.inspectMap.distance(last, latlng);
    this.measureCursorTooltip.setLatLng(latlng).setContent(this.formatDistance(total));
    if (!this.measureGroup.hasLayer(this.measureCursorTooltip)) {
      this.measureGroup.addLayer(this.measureCursorTooltip);
    }
  }

  /** Vertex dots, cumulative distance at intermediate vertices, and the total (with ×) at the last one. */
  private renderMeasureLabels() {
    this.measureLabels.clearLayers();
    let cumulative = 0;
    this.measurePoints.forEach((pt: any, i: number) => {
      if (i > 0) { cumulative += this.inspectMap.distance(this.measurePoints[i - 1], pt); }
      L.circleMarker(pt, {
        pane: 'measuretool', radius: 4, color: '#ffffff', weight: 2,
        fillColor: '#212121', fillOpacity: 1, interactive: false
      }).addTo(this.measureLabels);
      if (i > 0 && i < this.measurePoints.length - 1) {
        L.tooltip({ permanent: true, direction: 'right', offset: [8, 0], pane: 'measuretool', className: 'ctt-measure-label' } as any)
          .setLatLng(pt).setContent(this.formatDistance(cumulative)).addTo(this.measureLabels);
      }
    });
    // The total badge (with ×) appears only once there is a segment to measure.
    if (this.measurePoints.length < 2) { return; }
    const lastPt = this.measurePoints[this.measurePoints.length - 1];
    const text = 'Total: ' + this.formatDistance(cumulative);
    const totalMarker = L.marker(lastPt, {
      pane: 'measuretool',
      keyboard: false,
      icon: L.divIcon({
        className: 'ctt-measure-total',
        html: `<span>${text}</span><span class="ctt-measure-close" title="Remove measurement">&times;</span>`,
        iconSize: null,
        iconAnchor: [-10, 34]
      } as any)
    }).addTo(this.measureLabels);
    // Marker clicks don't bubble to the map, so the × never adds a point or triggers drill-down.
    totalMarker.on('click', (ev: any) => {
      const target: any = ev.originalEvent && ev.originalEvent.target;
      if (target && target.closest && target.closest('.ctt-measure-close')) { this.clearMeasure(); }
    });
  }

  /** Ends point entry; the measured line and its total stay on the map until × is clicked. */
  private finishMeasure() {
    if (!this.measuring) { return; }
    this.measuring = false;
    this.measureRubberLine.setLatLngs([]);
    if (this.measureCursorTooltip) { this.measureGroup.removeLayer(this.measureCursorTooltip); }
    this.restoreAfterMeasure();
    if (this.measurePoints.length < 2) { this.clearMeasure(); }
  }

  /** Removes the measurement entirely and restores map interactions. */
  private clearMeasure() {
    if (this.measuring) {
      this.measuring = false;
      this.restoreAfterMeasure();
    }
    if (this.measureGroup && this.inspectMap) { this.inspectMap.removeLayer(this.measureGroup); }
    this.measureGroup = null;
    this.measureLabels = null;
    this.measureLine = null;
    this.measureRubberLine = null;
    this.measureCursorTooltip = null;
    this.measurePoints = [];
  }

  private restoreAfterMeasure() {
    const mapObj = this.inspectMap;
    if (this.gisMapComp && this.gisMapComp.otherOptions) {
      this.gisMapComp.otherOptions.onLocationClick = this.savedOnLocationClick;
    }
    if (!mapObj) { return; }
    L.DomUtil.removeClass(mapObj.getContainer(), 'ctt-measuring');
    if (this.savedDoubleClickZoom) {
      // Re-enable after the current dblclick has finished dispatching so it doesn't also zoom.
      setTimeout(() => { if (mapObj.doubleClickZoom) { mapObj.doubleClickZoom.enable(); } }, 0);
    }
  }

  private measureDistance(points: any[]): number {
    let total = 0;
    for (let i = 1; i < points.length; i++) { total += this.inspectMap.distance(points[i - 1], points[i]); }
    return total;
  }

  private formatDistance(meters: number): string {
    return meters < 1000 ? Math.round(meters) + ' m' : (meters / 1000).toFixed(2) + ' km';
  }
}
